import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { listActivity, markRead } from "../../api/activity";
import type { ActivityItem, ActivityPage as Page, ActivityTab } from "../../api/activity";
import { claimCompany } from "../../api/claims";
import { errorText } from "../../api/client";
import { updateMe } from "../../api/me";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { EmptyState } from "../../components/EmptyState";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { formatDate, formatDateTime } from "../../lib/format";
import { ME_KEY, useMe } from "../auth/session";
import { ACTIVITY_KEY, useRefreshAfterChange } from "../claims/queries";
import { NotifySwitches } from "../profile/PreferenceFields";
import type { NotifyKey } from "../profile/PreferenceFields";
import styles from "./activity.module.css";

const t = nl.activity;
const TABS: readonly ActivityTab[] = ["all", "for_me", "followed", "claims"];

function dayLabel(day: string): string {
  const today = new Date();
  const iso = (d: Date) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Brussels" });
  if (day === iso(today)) return t.today;
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (day === iso(yesterday)) return t.yesterday;
  return formatDate(`${day}T12:00:00Z`);
}

/** Pages can split a day; merge consecutive days with the same date. */
function mergeDays(pages: Page[]): { date: string; items: ActivityItem[] }[] {
  const days: { date: string; items: ActivityItem[] }[] = [];
  for (const day of pages.flatMap((p) => p.days)) {
    const last = days.at(-1);
    if (last && last.date === day.date) last.items.push(...day.items);
    else days.push({ date: day.date, items: [...day.items] });
  }
  return days;
}

function itemText(item: ActivityItem): string {
  const company = item.company?.name ?? "";
  switch (item.kind) {
    case "claim_reminder":
      return t.texts.claim_reminder(company);
    case "claim_expired":
      return t.texts.claim_expired(company);
    case "waitlist_priority":
      return t.texts.waitlist_priority(
        company,
        typeof item.data.priority_until === "string" ? formatDateTime(item.data.priority_until) : "",
      );
    case "followed_report":
      return t.texts.followed_report(company);
    case "reminder_next_year":
      return t.texts.reminder_next_year(company);
    default:
      return t.unknown;
  }
}

function ClaimNow({ companyId, companyName }: { companyId: string; companyName: string }) {
  const toast = useToast();
  const refresh = useRefreshAfterChange();
  const claim = useMutation({
    mutationFn: () => claimCompany(companyId),
    onSuccess: (created) => {
      refresh();
      toast({ message: nl.claims.claimed(companyName, formatDate(created.expires_at)) });
    },
    onError: (error) => {
      toast({ message: errorText(error) });
    },
  });
  return (
    <Button
      variant="primary"
      disabled={claim.isPending || claim.isSuccess}
      onClick={() => {
        claim.mutate();
      }}
    >
      {t.claimNow}
    </Button>
  );
}

function Item({ item }: { item: ActivityItem }) {
  const until = typeof item.data.priority_until === "string" ? new Date(item.data.priority_until) : null;
  const canClaim = item.kind === "waitlist_priority" && item.company && until && until.getTime() > Date.now();
  return (
    <li className={[styles.item, !item.read && styles.unread].filter(Boolean).join(" ")}>
      {!item.read && <span className="sr-only">{t.unread}: </span>}
      <div className={styles.itemBody}>
        {item.company ? (
          <Link to={`/bedrijven/${item.company.id}`} className={styles.itemText}>
            {itemText(item)}
          </Link>
        ) : (
          <span className={styles.itemText}>{itemText(item)}</span>
        )}
        <span className={styles.time}>
          {new Date(item.created_at).toLocaleTimeString("nl-BE", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Europe/Brussels",
          })}
        </span>
      </div>
      {canClaim && item.company && <ClaimNow companyId={item.company.id} companyName={item.company.name} />}
    </li>
  );
}

function Preferences() {
  const me = useMe();
  const client = useQueryClient();
  const toast = useToast();
  const current = { claim: true, wait: true, follow: true, digest: true, email: false, ...me.data?.notify };
  const save = useMutation({
    mutationFn: (notify: Record<NotifyKey, boolean>) => updateMe({ notify }),
    onSuccess: (updated) => {
      client.setQueryData(ME_KEY, updated);
    },
  });
  if (!me.data) return null;
  return (
    <aside className={styles.prefs} aria-label={t.preferences}>
      <NotifySwitches
        legend={t.preferences}
        value={current}
        onChange={(next) => {
          const previous = current;
          save.mutate(next, {
            onSuccess: () => {
              toast({
                message: t.saved,
                onUndo: () => {
                  save.mutate(previous);
                },
              });
            },
          });
        }}
      />
    </aside>
  );
}

export function ActivityPage() {
  const client = useQueryClient();
  const [tab, setTab] = useState<ActivityTab>("all");
  const feed = useInfiniteQuery({
    queryKey: [...ACTIVITY_KEY, "feed", tab],
    queryFn: ({ pageParam }) => listActivity(tab, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
  const read = useMutation({
    mutationFn: markRead,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ACTIVITY_KEY });
    },
  });
  const days = feed.data ? mergeDays(feed.data.pages) : [];
  const unseen = days.flatMap((d) => d.items).filter((i) => !i.read).map((i) => i.id);
  const unseenKey = unseen.join(",");

  // What you have seen counts as read (after a moment, so the "Nieuw" mark is visible first).
  useEffect(() => {
    if (!unseenKey) return;
    const timer = window.setTimeout(() => {
      read.mutate(unseenKey.split(","));
    }, 1500);
    return () => {
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `read` is stable enough; rerun per set of ids
  }, [unseenKey]);

  let body;
  if (feed.isPending) {
    body = (
      <div className={styles.skeletons} role="status" aria-busy="true" aria-label={nl.common.loading}>
        <Skeleton height={18} width="30%" />
        <Skeleton height={56} />
        <Skeleton height={56} />
      </div>
    );
  } else if (feed.isError) {
    body = (
      <EmptyState
        title={t.loadError}
        action={<Button onClick={() => void feed.refetch()}>{nl.common.retry}</Button>}
      />
    );
  } else if (days.length === 0) {
    body = <EmptyState title={tab === "all" ? t.empty : t.emptyTab} />;
  } else {
    body = (
      <>
        {days.map((day) => (
          <section key={day.date} aria-labelledby={`day-${day.date}`} className={styles.day}>
            <h2 id={`day-${day.date}`} className={styles.dayTitle}>
              {dayLabel(day.date)}
            </h2>
            <ul className={styles.items}>
              {day.items.map((item) => (
                <Item key={item.id} item={item} />
              ))}
            </ul>
          </section>
        ))}
        {feed.hasNextPage && (
          <div className={styles.more}>
            <Button disabled={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
              {feed.isFetchingNextPage ? nl.common.loading : t.loadMore}
            </Button>
          </div>
        )}
      </>
    );
  }

  const unreadTotal = feed.data?.pages[0]?.unread_count ?? 0;
  return (
    <div className={styles.page}>
      <div className={styles.feed}>
        <div className={styles.header}>
          <h1 className={styles.title}>{nl.screens.activity}</h1>
          {unreadTotal > 0 && (
            <Button
              variant="ghost"
              onClick={() => {
                read.mutate("all");
              }}
            >
              {t.markAll}
            </Button>
          )}
        </div>
        <div className={styles.tabs} role="group" aria-label={t.tabsLabel}>
          {TABS.map((option) => (
            <Chip
              key={option}
              active={tab === option}
              onClick={() => {
                setTab(option);
              }}
            >
              {t.tabs[option]}
            </Chip>
          ))}
        </div>
        {body}
      </div>
      <Preferences />
    </div>
  );
}
